import { createFileRoute } from "@tanstack/react-router";
import { site } from "@/lib/site";

export const Route = createFileRoute("/refund")({
  head: () => ({
    meta: [
      { title: "Refund Policy - WeLivdIt" },
      {
        name: "description",
        content: "WeLivdIt's 30-day unused refund guarantee for Basic, Full, and Refusal Support visa coaching plans.",
      },
      { property: "og:title", content: "Refund Policy - WeLivdIt" },
      {
        property: "og:description",
        content:
          "100% refund if no session is conducted or taken by the WeLivdIt team. Read WeLivdIt's clear refund terms.",
      },
      { property: "og:url", content: "https://welivdit.com/refund" },
      { property: "og:image", content: "https://welivdit.com/og-share-v6.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:image", content: "https://welivdit.com/og-share-v6.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://welivdit.com/refund" }],
  }),
  component: RefundPage,
});

function RefundPage() {
  return (
    <section className="mx-auto max-w-3xl px-5 py-16 md:py-24">
      <p className="text-sm font-medium text-primary uppercase tracking-wider">Legal</p>
      <h1 className="mt-2 font-display text-4xl md:text-5xl font-semibold tracking-tight">
        Refund & Cancellation Policy
      </h1>
      <p className="mt-3 text-sm text-muted-foreground">Last updated: April 1, 2026</p>

      <div className="mt-10 space-y-8 text-foreground/80 leading-relaxed">
        <section className="rounded-2xl border border-primary/30 bg-primary/[0.04] p-6">
          <h2 className="font-display text-2xl font-semibold text-foreground">30-Day Unused Guarantee</h2>
          <p className="mt-2">
            You are eligible for a{" "}
            <strong>100% full refund if no session is conducted or taken by the WeLivdIt team</strong>. To qualify, you
            must request the cancellation within <strong>30 days of your payment</strong>, provided that{" "}
            <strong>no sessions of any kind</strong> (neither a DS-160 review nor a mock interview) have been conducted
            on your account.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl font-semibold text-foreground">1. Non-Refundable Conditions</h2>
          <p className="mt-2">
            Once <strong>any session has been conducted</strong>, fees become <strong>non-refundable</strong> across
            Basic Support, Full Support, and Refusal Support plans. This applies whether the session is a Refusal case
            analysis, DS-160 review, or a mock interview.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl font-semibold text-foreground">2. Case Example</h2>
          <blockquote className="mt-4 border-l-4 border-primary pl-4 py-2 italic text-foreground/70 bg-primary/[0.02] rounded-r-lg">
            "A student paid for Refusal Support, did not attend any sessions (NO DS-160 REVIEW / NO REFUSAL ANALYSIS /
            NO MOCKS), and requested cancellation within 30 days — a 100% full refund was processed."
          </blockquote>
        </section>

        <section>
          <h2 className="font-display text-2xl font-semibold text-foreground">3. How to Request</h2>
          <p className="mt-2">
            Send us your cancellation request with your payment reference ID. Approved refunds are processed within{" "}
            <strong>3–5 working days</strong> to the original payment method, subject to payment gateway rules.
          </p>
          <div className="mt-5 flex flex-wrap gap-4">
            <a
              href={`mailto:${site.email}?subject=Refund%20Request%20-%20%5BYour%20Payment%20Reference%20ID%5D&body=Hi%20WeLivdIt%20Team%2C%0A%0AI%20would%20like%20to%20request%20a%20cancellation%20and%20refund.%20My%20details%20are%20below%3A%0A%0AName%3A%20%0AEmail%3A%20%0APhone%20Number%3A%20%0APayment%20Reference%20ID%3A%20%0ADate%20of%20Plan%20Purchase%3A%20%0ANumber%20of%20Mocks%20Done%3A%20%0A%0AThank%20you.`}
              className="inline-flex items-center justify-center rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 shadow-sm"
            >
              Email Us
            </a>
            <a
              href="https://api.whatsapp.com/send/?phone=916300736712&text=Hi%20WeLivdIt%20Team%2C%20I%20would%20like%20to%20request%20a%20cancellation%20and%20refund.%20My%20details%20are%20below%3A%0A%0AName%3A%20%0AEmail%3A%20%0APhone%20Number%3A%20%0APayment%20Reference%20ID%3A%20%0ADate%20of%20Plan%20Purchase%3A%20%0ANumber%20of%20Mocks%20Done%3A%20"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center rounded-md border border-primary/20 bg-background px-5 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-primary/[0.04] shadow-sm"
            >
              WhatsApp Us
            </a>
          </div>
        </section>

        <section>
          <h2 className="font-display text-2xl font-semibold text-foreground">4. Rescheduling & No-Shows</h2>
          <p className="mt-2">
            Rescheduling requests must be made in advance. WeLivdIt accommodates genuine requests based on our team's
            availability. Repeated last-minute cancellations or no-shows may result in the session being marked as
            conducted. If you are running late, you must inform us before the session begins. Our team will wait in the
            meeting lobby for a maximum of <strong>5 minutes</strong> past the scheduled start time (e.g., until 6:05 PM
            for a 6:00 PM session). If you do not join within this grace period, the session will be marked as
            conducted.
          </p>
        </section>

        <section>
          <h2 className="font-display text-2xl font-semibold text-foreground">5. Disclaimer</h2>
          <p className="mt-2">
            WeLivdIt provides preparation, analysis, feedback, and guidance. We do not guarantee visa approval,
            interview outcomes, or embassy/consulate decisions. By purchasing a WeLivdIt plan, you agree to this policy.
          </p>
        </section>
      </div>
    </section>
  );
}
